import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, LogOut } from "lucide-react";

export const Route = createFileRoute("/no-access")({
  component: NoAccessPage,
  head: () => ({
    meta: [
      { title: "No workspace access — WMS" },
      { name: "description", content: "Your account has no workspace assignments. Contact your administrator." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function NoAccessPage() {
  const { user, loading, refresh, signOut, roles } = useAuth();
  const navigate = useNavigate();
  const [superExists, setSuperExists] = useState<boolean | null>(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (roles.length > 0) navigate({ to: "/" });
  }, [roles, navigate]);

  useEffect(() => {
    supabase.rpc("super_admin_exists").then(({ data, error }) => {
      if (error) {
        console.error(error);
        setSuperExists(true);
      } else setSuperExists(Boolean(data));
    });
  }, []);

  const claim = async () => {
    setClaiming(true);
    const { data, error } = await supabase.rpc("claim_super_admin");
    setClaiming(false);
    if (error) return toast.error(error.message);
    if (!data) {
      toast.error("A super admin already exists");
      setSuperExists(true);
      return;
    }
    toast.success("You are now the system super admin");
    await refresh();
    navigate({ to: "/" });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border rounded-xl p-8 shadow-sm text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-muted-foreground" />
        </div>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">No Access Yet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your account is signed in but hasn't been assigned a role. Ask a system admin to grant you access.
        </p>

        {superExists === false && (
          <div className="mt-6 rounded-lg border bg-muted/30 p-4 text-left">
            <p className="text-sm font-medium">First time setup</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No super admin exists yet. Claim it to bootstrap the system.
            </p>
            <Button onClick={claim} disabled={claiming} className="mt-3 w-full">
              {claiming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Claim super admin
            </Button>
          </div>
        )}

        <Button variant="ghost" className="mt-6" onClick={() => signOut().then(() => navigate({ to: "/login" }))}>
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}